import { useInvestorTransferTableDataRetrieveSuspense } from "gen/endpoints/investor-transfer-table/investor-transfer-table";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { get } from "lodash-es";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { LinkButton } from "shared/components/button/LinkButton";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";

export interface InvestorTransfersTableProps {
  fundFamilyId: string;
  transferCreatePath: string;
  isFullWidth?: boolean;
}

export const InvestorTransfersTable: React.FC<InvestorTransfersTableProps> = ({
  fundFamilyId,
  transferCreatePath,
  isFullWidth,
}) => {
  const { t } = useTranslation(["commitment", "common", "investorTransfer"]);

  const { data: investorTransferTableData } =
    useInvestorTransferTableDataRetrieveSuspense(fundFamilyId);

  return (
    <TableBox isFullWidth={isFullWidth}>
      <TableBoxHeader>
        <TableBoxTitle>{t("common:transfers")}</TableBoxTitle>
        <DataTableBoxActions
          createAction={
            <Restricted permission={PermissionTypeEnum.write}>
              <LinkButton size="xs" linkTo={transferCreatePath}>
                {t("investorTransfer:createTransfer")}
              </LinkButton>
            </Restricted>
          }
        />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={investorTransferTableData}
          getLinkInfoForRow={getLinkInfoForRow}
          emptyStateProps={{
            title: t("investorTransfer:empty.title"),
            description: t("investorTransfer:empty.description"),
            children: (
              <Restricted permission={PermissionTypeEnum.write}>
                <LinkButton size="sm" linkTo={transferCreatePath}>
                  {t("investorTransfer:createTransfer")}
                </LinkButton>
              </Restricted>
            ),
          }}
        />
      </TableBoxBody>
    </TableBox>
  );
};

const getLinkInfoForRow: GetLinkInfoForRowFn = ({ row }) => {
  const cell = get(row.data?.cells, "transferor");
  if (cell?.metadata?.type === "investor_transfer") {
    const transfersRoute = rootRoute({})
      .fundFamilies({})
      .fundFamily({ fundFamilyId: cell.metadata.fund_family_id })
      .investorTransfers({});
    const investorTransferId = cell.metadata.investor_transfer_id;
    return {
      type: "generic",
      to:
        cell.metadata.state === FundFamilyEventStateMachineFieldsEnum.draft ||
        cell.metadata.state ===
          FundFamilyEventStateMachineFieldsEnum.out_of_date
          ? transfersRoute.setup({ investorTransferId }).$
          : transfersRoute.view({ investorTransferId }).$,
    };
  }
  return undefined;
};
